import {
    Autocomplete,
    Box,
    Breadcrumbs,
    Stack,
    TextField,
    Typography,
} from "@mui/material";
import { useCompanies, useIsBasicRole } from "../../hooks";

import { BACKEND_ROUTES } from "../../backendRoutes";
import EditIcon from "@mui/icons-material/Edit";
import { FRONTEND_ROUTES } from "../../frontendRoutes";
import { PAGINATION } from "../../constants";
import { useNavigate } from "react-router-dom";
import useSWR from "swr";
import { useState } from "react";

export const NavigationBreadcrumbs = ({ company, contract, site }) => {
    const navigate = useNavigate();
    const [selectedBreadcrumb, setSelectedBreadcrumb] = useState();

    const {
        data: companies,
        error: companiesError,
        isLoading: areCompaniesLoading,
    } = useCompanies();
    const {
        data: contracts,
        error: contractsError,
        isLoading: areContractsLoading,
    } = useSWR(
        contract
            ? `${BACKEND_ROUTES.CONTRACT}?companyUuid=${contract.companyUuid}&limit=${PAGINATION.CONTRACT.LIMIT.MAX}`
            : null
    );
    const {
        data: sites,
        error: sitesError,
        isLoading: areSitesLoading,
    } = useSWR(
        site
            ? `${BACKEND_ROUTES.SITE}?contractUuid=${contract.uuid}&limit=${PAGINATION.SITE.LIMIT.MAX}`
            : null
    );

    const handleSelectCompanyChange = (companyUuid) => {
        setSelectedBreadcrumb();
        navigate(
            `/${FRONTEND_ROUTES.ORDERS}/${FRONTEND_ROUTES.CONTRACT}?companyUuid=${companyUuid}`
        );
    };
    const handleSelectContractChange = (contractUuid) => {
        setSelectedBreadcrumb();
        navigate(
            `/${FRONTEND_ROUTES.ORDERS}/${FRONTEND_ROUTES.CONTRACT}/${contractUuid}`
        );
    };
    const handleSelectSiteChange = (siteUuid) => {
        setSelectedBreadcrumb();
        navigate(
            `/${FRONTEND_ROUTES.ORDERS}/${FRONTEND_ROUTES.CONTRACT}/${contract.uuid}/${FRONTEND_ROUTES.SITE}/${siteUuid}`
        );
    };

    return (
        <Breadcrumbs>
            <BreadcrumbText
                data={companies}
                error={companiesError}
                isLoading={areCompaniesLoading}
                granularity="company"
                model={company}
                onChange={handleSelectCompanyChange}
                selectedBreadcrumb={selectedBreadcrumb}
                setSelectedBreadcrumb={setSelectedBreadcrumb}
                isEditable={false}
            />
            {contract && (
                <BreadcrumbText
                    data={contracts}
                    error={contractsError}
                    isLoading={areContractsLoading}
                    granularity="contract"
                    model={contract}
                    onChange={handleSelectContractChange}
                    selectedBreadcrumb={selectedBreadcrumb}
                    setSelectedBreadcrumb={setSelectedBreadcrumb}
                    isEditable={!site}
                />
            )}
            {site && (
                <BreadcrumbText
                    data={sites}
                    error={sitesError}
                    isLoading={areSitesLoading}
                    granularity="site"
                    model={site}
                    onChange={handleSelectSiteChange}
                    selectedBreadcrumb={selectedBreadcrumb}
                    setSelectedBreadcrumb={setSelectedBreadcrumb}
                    isEditable={true}
                />
            )}
        </Breadcrumbs>
    );
};

const BreadcrumbText = ({
    data,
    error,
    isLoading,
    granularity,
    model,
    onChange,
    selectedBreadcrumb,
    setSelectedBreadcrumb,
    isEditable,
}) => {
    const isBasicRole = useIsBasicRole();
    const displaySelect = !error && !isLoading && data?.rows;

    const canEdit =
        isEditable && displaySelect && selectedBreadcrumb === granularity;

    return canEdit ? (
        <Box sx={{ minWidth: 200 }}>
            <Autocomplete
                id={granularity}
                options={data.rows}
                getOptionLabel={(option) =>
                    granularity === "site" && isBasicRole
                        ? option.displayName
                        : option.name
                }
                isOptionEqualToValue={(option, value) =>
                    option.uuid === value.uuid
                }
                value={model}
                fullWidth
                onChange={(_, newValue) => {
                    if (newValue) {
                        onChange(newValue.uuid);
                    }
                }}
                onBlur={() => setSelectedBreadcrumb()}
                renderInput={(params) => (
                    <TextField {...params} variant="standard" autoFocus />
                )}
            />
        </Box>
    ) : (
        <Stack
            direction={"row"}
            spacing={1}
            alignItems={"center"}
            sx={{ cursor: "pointer" }}
            onClick={() =>
                isEditable
                    ? setSelectedBreadcrumb(granularity)
                    : onChange(model.uuid)
            }
        >
            <Typography variant="h5">
                {granularity === "site" && isBasicRole
                    ? model.displayName
                    : model.name}
            </Typography>
            {isEditable && <EditIcon fontSize="small" />}
        </Stack>
    );
};
